import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ListCard from "../components/ListCard";
import { GET_TRUST_CENTER_ACTIVITY } from "../../../../apis/trust-center-apis/trustCenterApis";
import { includesQuery } from "../../../Organization/CodeSecurity/codeSecurityShared";
import usePresentFilter from "../../hooks/usePresentFilter";
import RefreshButton from "../../components/RefreshButton";
import {
  LOG_TABS,
  eventLabel,
  logDetailLines,
  logDetails,
  logEventLabels,
} from "../../data/logs";

const PAGE_SIZE = 200;

const EVENT_FILTERS = Object.fromEntries(
  Object.entries(logEventLabels).map(([event, label]) => [
    label,
    (row) => row.event === event,
  ])
);

const COLUMNS = [
  { label: "What happened", field: "event", width: "24%" },
  { label: "Item", field: "item", width: "32%" },
  { label: "Who", field: "who", width: "26%" },
  { label: "When", field: "createdAt", width: "18%", sortable: true },
];

const SUBTITLES = {
  all: "What visitors did, and what went live when you published.",
  visitors: "Documents opened and requested, and access granted or revoked.",
  resources: "Resources and categories that went live.",
  compliance: "Frameworks shown, hidden, or edited on the live page.",
  controls: "Which controls visitors see.",
  subprocessors: "Vendors shown, hidden, or edited on the live page.",
  faq: "Questions that went live.",
  updates: "Posts that went live.",
  publishing: "Each time the page went live or was taken offline.",
};

const EMPTY = {
  all: "Nothing logged yet.",
  visitors: "Nobody has visited yet. Share your Trust Center link with a buyer.",
  publishing: "Not published yet.",
};

const LOG_PILL_SX = {
  flexWrap: "wrap",
  gap: 0.75,
  overflow: "visible",
  "& .MuiToggleButtonGroup-grouped": {
    border: "1px solid #E8EAED !important",
    borderRadius: "36px !important",
    margin: "0 !important",
  },
  "& .MuiToggleButton-root": {
    border: "1px solid #E8EAED",
    borderRadius: "36px",
    color: "#6B7280",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    px: 1.75,
    py: 0.5,
    minHeight: 32,
    "&.Mui-selected": {
      backgroundColor: "#3B82F6",
      color: "#fff",
      borderColor: "#3B82F6",
      "&:hover": { backgroundColor: "#2563EB" },
    },
    "&:hover:not(.Mui-selected)": {
      backgroundColor: "#F9FAFB",
      color: "#374151",
    },
    "&.Mui-focusVisible": {
      outline: "2px solid #3B82F6",
      outlineOffset: 2,
    },
  },
};

const formatDate = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const Line = ({ children, muted, strong }) => {
  const theme = useTheme();
  const text = children || "—";
  return (
    <Typography
      variant="body2"
      noWrap
      title={typeof text === "string" ? text : undefined}
      sx={{
        fontWeight: strong ? 500 : 400,
        color: muted ? theme.customColors.text.secondary : undefined,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {text}
    </Typography>
  );
};

const LogsTab = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const logTab = LOG_TABS.some((t) => t.value === searchParams.get("log"))
    ? searchParams.get("log")
    : "all";
  const setLogTab = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("log", value);
    setSearchParams(next, { replace: true });
    setSearch("");
    setFilter(null);
    setOpenId(null);
  };

  const { data, error, loading } = useQuery(GET_TRUST_CENTER_ACTIVITY, {
    variables: {
      limit: PAGE_SIZE,
      offset: 0,
      group: logTab === "all" ? null : logTab,
    },
    fetchPolicy: "cache-and-network",
  });
  const page = data?.getTrustCenterActivity;
  const log = useMemo(
    () =>
      (page?.items || []).map((item) => {
        const by =
          item.actor === "admin" ? item.byEmail || item.email : item.byEmail;
        const lines = logDetailLines(item.event, item.metadata);
        if (item.ipAddress) lines.push({ label: "IP address", value: item.ipAddress });
        if (item.actor === "admin" && by && item.email && item.email !== by)
          lines.push({ label: "Visitor", value: item.email });
        return {
          id: item.id,
          actor: item.actor,
          email: item.email,
          event: item.event,
          item: item.resourceName,
          details: logDetails(item.event, item.metadata),
          lines,
          by,
          who:
            item.actor === "admin"
              ? by || "Your team"
              : item.email || "Anonymous visitor",
          createdAt: item.createdAt,
        };
      }),
    [page]
  );
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [sortBy, setSortBy] = useState(["-createdAt"]);
  const onSortChange = (next) => {
    setSortBy(next.length === 0 ? ["createdAt"] : next);
  };
  const { filter, setFilter, options: filterOptions } = usePresentFilter(
    log,
    EVENT_FILTERS
  );

  const rows = useMemo(() => {
    const filtered = log.filter(
      (entry) =>
        (!filter || EVENT_FILTERS[filter](entry)) &&
        includesQuery(
          search,
          entry.email,
          entry.item,
          entry.by,
          entry.who,
          entry.details,
          ...entry.lines.map((line) => `${line.label} ${line.value}`)
        )
    );
    const desc = sortBy[0] !== "createdAt";
    return [...filtered].sort((a, b) => {
      const av = new Date(a.createdAt).getTime();
      const bv = new Date(b.createdAt).getTime();
      return desc ? bv - av : av - bv;
    });
  }, [log, search, filter, sortBy]);

  const muted = { color: theme.customColors.text.secondary };
  const t = theme.customColors;
  const toggleOpen = (id) => setOpenId((current) => (current === id ? null : id));

  const renderCell = (row, column) => {
    switch (column.field) {
      case "event":
        return (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
            {row.lines.length > 0 ? (
              <IconButton
                size="small"
                aria-label={`${openId === row.id ? "Hide" : "Show"} details of ${eventLabel(row.event)}`}
                aria-expanded={openId === row.id}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleOpen(row.id);
                }}
                sx={{ color: t.icons.dark, flexShrink: 0 }}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 20,
                    transform: openId === row.id ? "rotate(180deg)" : "none",
                    transition: "transform 160ms ease",
                    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                  }}
                />
              </IconButton>
            ) : (
              <Box sx={{ width: 34, flexShrink: 0 }} />
            )}
            <Line strong>{eventLabel(row.event)}</Line>
          </Stack>
        );
      case "item":
        return (
          <Box sx={{ minWidth: 0 }}>
            <Line strong>{row.item}</Line>
            {row.details && <Line muted>{row.details}</Line>}
          </Box>
        );
      case "who":
        return (
          <Box sx={{ minWidth: 0 }}>
            <Line>{row.who}</Line>
            {row.actor === "admin" && row.email && row.email !== row.who && (
              <Line muted>Visitor {row.email}</Line>
            )}
          </Box>
        );
      case "createdAt":
        return <Line muted>{formatDate(row.createdAt)}</Line>;
      default:
        return row[column.field] || "—";
    }
  };

  const renderRowFooter = (row) =>
    openId === row.id ? (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          pl: 7,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: t.background.light,
        }}
      >
        <Box
          component="dl"
          role="group"
          aria-label="Event details"
          sx={{
            m: 0,
            display: "grid",
            gridTemplateColumns: "140px 1fr",
            columnGap: 2,
            rowGap: 0.75,
          }}
        >
          {row.lines.map((line) => (
            <React.Fragment key={`${line.label}-${line.value}`}>
              <Typography
                component="dt"
                variant="body2"
                sx={{ ...muted, m: 0 }}
              >
                {line.label}
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                {line.value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    ) : null;

  const pills = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={logTab}
      onChange={(_, value) => {
        if (value) setLogTab(value);
      }}
      aria-label="Logs"
      sx={LOG_PILL_SX}
    >
      {LOG_TABS.map((tab) => (
        <ToggleButton key={tab.value} value={tab.value}>
          {tab.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  if (error && !page) {
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: "12px", alignItems: "center" }}
        action={<RefreshButton label="Logs" queries={["getTrustCenterActivity"]} />}
      >
        Could not load the logs. Click refresh to try again.
      </Alert>
    );
  }

  return (
    <ListCard
      title="Logs"
      toolbar={pills}
      refreshQueries={["getTrustCenterActivity"]}
      subtitle={`${SUBTITLES[logTab]}${
        page && page.total > log.length
          ? ` Showing the latest ${log.length} of ${page.total} events.`
          : ""
      }`}
      searchQuery={search}
      onSearchChange={setSearch}
      filters={filterOptions}
      selectedFilter={filter}
      onFilterChange={setFilter}
      onClearFilters={() => {
        setSearch("");
        setFilter(null);
      }}
      countNoun="event"
      columns={COLUMNS}
      rows={rows}
      loading={loading && !page}
      renderCell={renderCell}
      renderRowFooter={renderRowFooter}
      getRowSx={(row) => ({
        cursor: row.lines.length ? "pointer" : "default",
        backgroundColor: openId === row.id ? t.background.light : undefined,
        "&:hover": {
          backgroundColor: t.background.light,
        },
      })}
      getRowProps={(row) =>
        row.lines.length
          ? {
              onClick: () => toggleOpen(row.id),
            }
          : {}
      }
      sortBy={sortBy}
      onSortChange={onSortChange}
      emptyText={EMPTY[logTab] || "Nothing logged here yet."}
    />
  );
};

export default LogsTab;
